import { trackDownload } from '../utils/downloadIndicator';
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Chip,
    IconButton,
    Checkbox,
    FormControlLabel,
    Snackbar,
    Paper,
    CircularProgress,
    Collapse,
    useTheme
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Assignment as AssignmentIcon,
    Clear as ClearIcon,
    Delete as DeleteIcon,
    PictureAsPdf as PdfIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    CalendarToday as CalendarTodayIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    OpenInNew as OpenInNewIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import SignatureCanvas from 'react-signature-canvas';
import PhotoUploadField from './PhotoUploadField';
import PdfUploadField from './PdfUploadField';
import TableField from './TableField';
import { formTemplatesApi, projectFormsApi } from '../services/api';

const getDateInputValue = (dateValue) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getDefaultFormDateRange = () => {
    const endDate = new Date();
    const startDate = new Date(endDate);
    startDate.setFullYear(startDate.getFullYear() - 1);

    return {
        startDate: getDateInputValue(startDate),
        endDate: getDateInputValue(endDate),
    };
};

const ProjectFormsTab = ({ projectId, openFormId = null, onOpenFormHandled, onReturnToOrigin, onOpenActivity }) => {
    const theme = useTheme();
    const [forms, setForms] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Dialog states
    const [addFormDialogOpen, setAddFormDialogOpen] = useState(false);
    const [viewFormDialogOpen, setViewFormDialogOpen] = useState(false);
    const [deleteFormDialogOpen, setDeleteFormDialogOpen] = useState(false);
    const [isAddingForm, setIsAddingForm] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [selectedForm, setSelectedForm] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formToDelete, setFormToDelete] = useState(null);
    const [discardNavigationDialogOpen, setDiscardNavigationDialogOpen] = useState(false);
    const [pendingNavigationForm, setPendingNavigationForm] = useState(null);
    const [isNavigatingForms, setIsNavigatingForms] = useState(false);
    
    // Form submission state
    const [formResponses, setFormResponses] = useState({});
    const [formTitle, setFormTitle] = useState('');
    const defaultDateRange = getDefaultFormDateRange();
    const [titleFilter, setTitleFilter] = useState('');
    const [startDateFilter, setStartDateFilter] = useState(defaultDateRange.startDate);
    const [endDateFilter, setEndDateFilter] = useState(defaultDateRange.endDate);

    // Signature dialog state
    const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
    const [currentSignatureField, setCurrentSignatureField] = useState(null);
    const signatureRef = useRef(null);
    // New state for signature name and validation
    const [signatureName, setSignatureName] = useState('');
    const [openingFormId, setOpeningFormId] = useState(null);
    const [collapsedFormTypes, setCollapsedFormTypes] = useState({});

    useEffect(() => {
        if (projectId) {
            loadProjectForms();
            loadFormTemplates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    // Handle signature canvas resize when dialog opens
    useEffect(() => {
        const handleResize = () => {
            if (signatureRef.current && signatureDialogOpen) {
                const canvas = signatureRef.current.getCanvas();
                const container = canvas.parentElement;
                if (container) {
                    const containerRect = container.getBoundingClientRect();
                    const containerWidth = containerRect.width;
                    
                    canvas.width = containerWidth;
                    canvas.height = 200;
                    
                    canvas.style.width = '100%';
                    canvas.style.height = '200px';
                    
                    signatureRef.current.clear();
                }
            }
        };

        if (signatureDialogOpen) {
            window.addEventListener('resize', handleResize);
            setTimeout(handleResize, 50);
            setTimeout(handleResize, 150);
            setTimeout(handleResize, 300);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [signatureDialogOpen]);

    useEffect(() => {
        if (!openFormId || forms.length === 0) {
            return;
        }

        const matchingForm = forms.find(form => form.project_form_id === openFormId);
        if (matchingForm) {
            handleViewForm(matchingForm);
            onOpenFormHandled?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openFormId, forms]);

    const loadProjectForms = async () => {
        try {
            setLoading(true);
            setError(''); // Clear any previous errors
            const response = await projectFormsApi.getByProjectId(projectId, { summary: true });
            
            // Sort forms by creation date to ensure consistent ordering
            const sortedForms = response.data.sort((a, b) => {
                return new Date(a.created_date || a.saved_date) - new Date(b.created_date || b.saved_date);
            });
            
            // Force React to recognize this as a completely new array
            setForms([...sortedForms]);
        } catch (err) {
            setError('Failed to load project forms');
            console.error('Error loading project forms:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadFormTemplates = async () => {
        try {
            const response = await formTemplatesApi.getAll();
            setTemplates(response.data);
        } catch (err) {
            setError('Failed to load form templates');
            console.error('Error loading templates:', err);
        }
    };

    const handleAddForm = async () => {
        try {
            if (!selectedTemplate) {
                setError('Please select a form template');
                return;
            }

            setIsAddingForm(true);

            const template = templates.find(t => t.template_id === parseInt(selectedTemplate));
            if (!template) {
                setError('Selected template not found');
                return;
            }

            // Initialize form responses with empty values
            const initialFormData = {};
            template.form_schema.fields.forEach(field => {
                initialFormData[field.id] = '';
            });

            const formData = {
                template_id: parseInt(selectedTemplate),
                form_data: initialFormData
            };

            await projectFormsApi.create(projectId, formData);
            setSuccess('Form added to project successfully');
            setAddFormDialogOpen(false);
            setSelectedTemplate('');
            
            // Clear any cached form state
            setSelectedForm(null);
            setFormResponses({});
            setIsEditing(false);
            
            // Force a fresh reload of forms to show the new addition
            await loadProjectForms();
        } catch (err) {
            setError('Failed to add form to project');
            console.error('Error adding form:', err);
        } finally {
            setIsAddingForm(false);
        }
    };

    const handleViewForm = async (form) => {
        try {
            setOpeningFormId(form.project_form_id);
            let editableForm = form;
            const hasFullDetails = !!(
                form.form_data &&
                (form.template?.form_schema?.fields || form.form_schema?.fields || form.template_form_schema?.fields)
            );

            if (!hasFullDetails) {
                const response = await projectFormsApi.getById(projectId, form.project_form_id);
                editableForm = response.data;
            }

            setSelectedForm(editableForm);
            setFormResponses(editableForm.form_data || {});
            setFormTitle(editableForm.title || '');

            const progress = getFormProgress(editableForm);
            const isComplete = progress === 100;

            setIsEditing(!isComplete);
            setViewFormDialogOpen(true);
        } catch (err) {
            setError('Failed to load form details');
            console.error('Error loading form details:', err);
        } finally {
            setOpeningFormId(null);
        }
    };

    const handleOpenLinkedActivity = (form, activityId = form?.activity_id || form?.activity_ids?.[0]) => {
        if (!activityId || !onOpenActivity) return;
        setViewFormDialogOpen(false);
        setSelectedForm(null);
        setFormResponses({});
        setFormTitle('');
        setIsEditing(false);
        onOpenActivity(activityId);
    };

    const getActivityLabel = (form) => (
        form?.activity_title || `Activity #${form?.activity_id || form?.activity_ids?.[0]}`
    );

    const getDateFilterValue = (dateValue) => {
        if (!dateValue) return '';

        const date = new Date(dateValue);
        if (Number.isNaN(date.getTime())) return '';

        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const isWithinDateRange = (dateValue, startDate, endDate) => {
        if (!startDate && !endDate) return true;

        const normalizedDate = getDateFilterValue(dateValue);
        if (!normalizedDate) return false;
        if (startDate && normalizedDate < startDate) return false;
        if (endDate && normalizedDate > endDate) return false;
        return true;
    };

    const getSelectedFormIndex = () => {
        if (!selectedForm) return -1;
        return filteredForms.findIndex(form => form.project_form_id === selectedForm.project_form_id);
    };

    const hasUnsavedChanges = () => {
        if (!selectedForm || !isEditing) return false;

        return (
            (selectedForm.title || '') !== formTitle ||
            JSON.stringify(selectedForm.form_data || {}) !== JSON.stringify(formResponses || {})
        );
    };

    const openFormInDialog = async (form) => {
        if (!form) return;
        setIsNavigatingForms(true);
        try {
            await handleViewForm(form);
        } finally {
            setIsNavigatingForms(false);
        }
    };

    const handleNavigateForm = async (direction) => {
        const currentIndex = getSelectedFormIndex();
        const nextIndex = currentIndex + direction;

        if (currentIndex === -1 || nextIndex < 0 || nextIndex >= filteredForms.length) {
            return;
        }

        const nextForm = filteredForms[nextIndex];

        if (hasUnsavedChanges()) {
            setPendingNavigationForm(nextForm);
            setDiscardNavigationDialogOpen(true);
            return;
        }

        await openFormInDialog(nextForm);
    };

    const handleDiscardNavigationCancel = () => {
        setDiscardNavigationDialogOpen(false);
        setPendingNavigationForm(null);
    };

    const handleDiscardNavigationConfirm = async () => {
        const nextForm = pendingNavigationForm;
        setDiscardNavigationDialogOpen(false);
        setPendingNavigationForm(null);
        await openFormInDialog(nextForm);
    };

    const handleEditForm = () => {
        setIsEditing(true);
    };

    const valuesAreEqual = (left, right) => {
        if (left === right) return true;

        const leftIsEmpty = left === undefined || left === null || left === '';
        const rightIsEmpty = right === undefined || right === null || right === '';
        if (leftIsEmpty || rightIsEmpty) return leftIsEmpty && rightIsEmpty;

        if (typeof left === 'object' || typeof right === 'object') {
            try {
                return JSON.stringify(left) === JSON.stringify(right);
            } catch (error) {
                return false;
            }
        }

        return String(left) === String(right);
    };

    const syncSavedFormState = (formId, nextResponses, nextTitle) => {
        const savedDate = new Date().toISOString();

        setSelectedForm(prev => (
            prev && prev.project_form_id === formId
                ? {
                    ...prev,
                    form_data: nextResponses,
                    title: nextTitle,
                    saved_date: savedDate,
                }
                : prev
        ));

        setForms(prevForms => prevForms.map(form => (
            form.project_form_id === formId
                ? {
                    ...form,
                    form_data: nextResponses,
                    title: nextTitle,
                    saved_date: savedDate,
                }
                : form
        )));
    };

    const saveFormDraft = async ({ nextResponses = formResponses, nextTitle = formTitle, force = false, showSuccess = false } = {}) => {
        if (!selectedForm?.project_form_id) return;

        const savedResponses = selectedForm.form_data || {};
        const savedTitle = selectedForm.title || '';
        const hasChanges = !valuesAreEqual(nextTitle || '', savedTitle) || !valuesAreEqual(nextResponses || {}, savedResponses);

        if (!force && !hasChanges) {
            return;
        }

        await projectFormsApi.update(projectId, selectedForm.project_form_id, {
            form_data: nextResponses,
            title: nextTitle,
        });

        syncSavedFormState(selectedForm.project_form_id, nextResponses, nextTitle);

        if (showSuccess) {
            setSuccess('Form updated successfully');
        }
    };

    const handleSaveForm = async () => {
        try {
            await saveFormDraft({ force: true, showSuccess: true });
            setIsEditing(false);
            setViewFormDialogOpen(false);
            loadProjectForms();
            onReturnToOrigin?.();
        } catch (err) {
            setError('Failed to save form');
            console.error('Error saving form:', err);
        }
    };

    const closeFormDialog = () => {
        setViewFormDialogOpen(false);
        setSelectedForm(null);
        setFormResponses({});
        setFormTitle('');
        setIsEditing(false);
        onReturnToOrigin?.();
    };

    const handleCancelEdit = () => {
        setFormResponses(selectedForm.form_data || {});
        setFormTitle(selectedForm.title || '');
        setIsEditing(false);
    };

    const handleDeleteClick = (form) => {
        setFormToDelete(form);
        setDeleteFormDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            if (!formToDelete) return;
            
            await projectFormsApi.delete(projectId, formToDelete.project_form_id);
            setSuccess('Form deleted successfully');
            setDeleteFormDialogOpen(false);
            setFormToDelete(null);
            
            // Reload forms to reflect the deletion
            await loadProjectForms();
        } catch (err) {
            setError('Failed to delete form');
            console.error('Error deleting form:', err);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteFormDialogOpen(false);
        setFormToDelete(null);
    };

    const handleExportPDF = trackDownload(async (form) => {
        try {
            const response = await projectFormsApi.exportPDF(projectId, form.project_form_id);
            
            // Create blob URL and download
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Generate filename
            const templateName = form.template_name.replace(/[^a-zA-Z0-9]/g, '_');
            const timestamp = new Date().toISOString().split('T')[0];
            link.download = `Form_${templateName}_${timestamp}.pdf`;
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            setSuccess('Form exported as PDF successfully');
        } catch (err) {
            setError('Failed to export form as PDF');
            console.error('Error exporting PDF:', err);
        }
    });

    const handleFieldChange = (fieldId, value) => {
        setFormResponses(prev => ({
            ...prev,
            [fieldId]: value
        }));
    };

    const handleFieldCommit = async (fieldId, value) => {
        const nextResponses = {
            ...formResponses,
            [fieldId]: value,
        };

        handleFieldChange(fieldId, value);

        try {
            await saveFormDraft({ nextResponses });
        } catch (err) {
            console.error('Error auto-saving form field:', err);
        }
    };

    const handleTitleCommit = async (value) => {
        try {
            await saveFormDraft({ nextResponses: formResponses, nextTitle: value });
        } catch (err) {
            console.error('Error auto-saving form title:', err);
        }
    };

    const handleSignatureClick = (field) => {
        setCurrentSignatureField(field);
        // Prefill signer name if existing
        const existingValue = formResponses[field.id];
        if (existingValue && typeof existingValue === 'object') {
            setSignatureName(existingValue.name || existingValue.signer_name || existingValue.printed_name || '');
        } else {
            setSignatureName('');
        }
        setSignatureDialogOpen(true);
    };

    const handleSignatureDialogClose = () => {
        setSignatureDialogOpen(false);
        setCurrentSignatureField(null);
        setSignatureName('');
        if (signatureRef.current) {
            signatureRef.current.clear();
        }
    };

    const handleSaveSignature = () => {
        // Require both a drawn signature and a name
        if (!signatureName.trim()) {
            setError('Please enter the signer\'s name before saving the signature');
            return;
        }
        if (!signatureRef.current || signatureRef.current.isEmpty()) {
            setError('Please provide a signature before saving');
            return;
        }
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
            const signatureDataURL = signatureRef.current.toDataURL();
            handleFieldChange(currentSignatureField.id, { signature: signatureDataURL, name: signatureName.trim() });
            handleSignatureDialogClose();
        }
    };

    const handleClearSignature = () => {
        if (signatureRef.current) {
            signatureRef.current.clear();
        }
    };

    const getFieldValue = (values, fieldId) => {
        if (!values || typeof values !== 'object') return undefined;
        return values[fieldId] ?? values[String(fieldId)];
    };

    const renderFormField = (field, value, isEditable) => {
        // For signature field, value may be object { signature, name }
        const fieldValue = value || '';

        switch (field.type) {
            case 'text':
                return (
                    <TextField
                        fullWidth
                        value={fieldValue}
                        onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        onBlur={(e) => handleFieldCommit(field.id, e.target.value)}
                        disabled={!isEditable}
                        size="small"
                    />
                );

            case 'textarea':
                return (
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={fieldValue}
                        onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        onBlur={(e) => handleFieldCommit(field.id, e.target.value)}
                        disabled={!isEditable}
                        size="small"
                    />
                );

            case 'select':
                return (
                    <FormControl fullWidth size="small">
                        <Select
                            value={fieldValue}
                            onChange={(e) => handleFieldCommit(field.id, e.target.value)}
                            disabled={!isEditable}
                        >
                            <MenuItem value="">Select an option</MenuItem>
                            {field.options?.map((option, index) => (
                                <MenuItem key={index} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );

            case 'checkbox':
                return (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={fieldValue === true || fieldValue === 'true'}
                                onChange={(e) => handleFieldCommit(field.id, e.target.checked)}
                                disabled={!isEditable}
                            />
                        }
                        label={field.label}
                    />
                );

            case 'date':
                return (
                    <TextField
                        fullWidth
                        type="date"
                        value={fieldValue}
                        onChange={(e) => handleFieldCommit(field.id, e.target.value)}
                        disabled={!isEditable}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                );

            case 'number':
                return (
                    <TextField
                        fullWidth
                        type="number"
                        value={fieldValue}
                        onChange={(e) => handleFieldCommit(field.id, e.target.value)}
                        disabled={!isEditable}
                        size="small"
                    />
                );

            case 'signature':
                const signatureImage = (fieldValue && typeof fieldValue === 'object') ? fieldValue.signature : fieldValue;
                const signerName = (fieldValue && typeof fieldValue === 'object') ? (fieldValue.name || fieldValue.signer_name || fieldValue.printed_name) : '';
                return (
                    <Box sx={(theme) => ({
                        border: `2px dashed ${theme.palette.divider}`,
                        height: 170,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isEditable ? 'background.paper' : 'background.default',
                        cursor: isEditable ? 'pointer' : 'default',
                        borderRadius: 1,
                        position: 'relative',
                        p: 1
                    })}
                    onClick={() => isEditable && handleSignatureClick(field)}
                    >
                        {signatureImage ? (
                            <Box sx={{ 
                                width: '100%', 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                            }}>
                                <img 
                                    src={signatureImage} 
                                    alt="Signature" 
                                    style={{ 
                                        maxWidth: '100%', 
                                        maxHeight: '100%',
                                        objectFit: 'contain'
                                    }} 
                                />
                                {signerName && (
                                    <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        Signed by: {signerName}
                                    </Typography>
                                )}
                                {isEditable && (
                                    <Box sx={{ 
                                        position: 'absolute',
                                        top: 4,
                                        right: 4
                                    }}>
                                            <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleFieldCommit(field.id, '');
                                            }}
                                            sx={{ 
                                                bgcolor: 'background.paper',
                                                boxShadow: 1,
                                                '&:hover': { bgcolor: (theme) => theme.palette.action.hover }
                                            }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Typography color="text.secondary" align="center">
                                {isEditable ? 'Click to Sign' : 'No signature'}
                            </Typography>
                        )}
                    </Box>
                );

            case 'photo':
                return (
                    <Box>
                        <PhotoUploadField
                            field={field}
                            value={fieldValue}
                            onChange={handleFieldCommit}
                            projectId={projectId}
                            formId={selectedForm?.project_form_id}
                            disabled={!isEditable}
                        />
                    </Box>
                );

            case 'pdf':
                return (
                    <Box>
                        <PdfUploadField
                            field={field}
                            value={fieldValue}
                            onChange={handleFieldCommit}
                            projectId={projectId}
                            formId={selectedForm?.project_form_id}
                            disabled={!isEditable}
                        />
                    </Box>
                );

            case 'table':
                return (
                    <Box>
                        <TableField
                            field={field}
                            value={fieldValue}
                            onChange={handleFieldCommit}
                            disabled={!isEditable}
                            isEditing={isEditable}
                        />
                    </Box>
                );

            default:
                return (
                    <TextField
                        fullWidth
                        value={fieldValue}
                        onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        onBlur={(e) => handleFieldCommit(field.id, e.target.value)}
                        disabled={!isEditable}
                        size="small"
                    />
                );
        }
    };

    const getFormProgress = (form) => {
        const numericProgress = Number(form.progress_percentage);
        if (Number.isFinite(numericProgress)) return Math.round(numericProgress);

        const schema = form.form_schema || form.template?.form_schema || form.template_form_schema;
        if (!schema?.fields) return 0;
        
        // Only count required fields
        const requiredFields = schema.fields.filter(field => field.required);
        const totalRequiredFields = requiredFields.length;

        if (totalRequiredFields === 0) return 100;
        
        const completedFields = requiredFields.filter(field => {
            const value = getFieldValue(form.form_data, field.id);
            if (value === undefined || value === null || value === '') return false;
            if (field.type === 'signature' && typeof value === 'object' && !value.signature) return false;
            if (field.type === 'photo' && (!Array.isArray(value) || value.length === 0)) return false;
            return true;
        }).length;
        
        return Math.round((completedFields / totalRequiredFields) * 100);
    };

    const filteredForms = forms.filter((form) => {
        const normalizedTitle = (form.title || '').trim().toLowerCase();
        const normalizedFilter = titleFilter.trim().toLowerCase();
        const matchesTitle = !normalizedFilter || normalizedTitle.includes(normalizedFilter);
        const matchesDate = isWithinDateRange(form.saved_date || form.created_date, startDateFilter, endDateFilter);
        return matchesTitle && matchesDate;
    });
    const formsByType = Object.values(filteredForms.reduce((groups, form) => {
        const key = form.template_id ?? form.template_name ?? 'other';
        if (!groups[key]) groups[key] = { key, name: form.template_name || 'Other forms', forms: [] };
        groups[key].forms.push(form);
        return groups;
    }, {}));

    const selectedFormIndex = getSelectedFormIndex();
    const canNavigateToPreviousForm = selectedFormIndex > 0;
    const canNavigateToNextForm = selectedFormIndex !== -1 && selectedFormIndex < filteredForms.length - 1;

    const hasActiveFilters = Boolean(
        titleFilter ||
        startDateFilter !== defaultDateRange.startDate ||
        endDateFilter !== defaultDateRange.endDate
    );

    const clearFilters = () => {
        setTitleFilter('');
        setStartDateFilter(defaultDateRange.startDate);
        setEndDateFilter(defaultDateRange.endDate);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1, minWidth: 280 }}>
                    <TextField
                        size="small"
                        placeholder="Filter by form title"
                        value={titleFilter}
                        onChange={(e) => setTitleFilter(e.target.value)}
                        inputProps={{ 'aria-label': 'Filter by form title' }}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ fontSize: '18px', color: 'text.secondary', mr: 1 }} />,
                        }}
                        sx={{ minWidth: 220, flex: 1 }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <CalendarTodayIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
                        <TextField
                            size="small"
                            type="date"
                            value={startDateFilter}
                            onChange={(e) => setStartDateFilter(e.target.value)}
                            inputProps={{ 'aria-label': 'Form date from' }}
                            sx={{ width: 150 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            to
                        </Typography>
                        <TextField
                            size="small"
                            type="date"
                            value={endDateFilter}
                            onChange={(e) => setEndDateFilter(e.target.value)}
                            inputProps={{ 'aria-label': 'Form date to' }}
                            sx={{ width: 150 }}
                        />
                        {hasActiveFilters && (
                            <IconButton
                                size="small"
                                onClick={clearFilters}
                                aria-label="Clear form filters"
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setAddFormDialogOpen(true)}
                >
                    Add Form
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {forms.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No forms added yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Add forms to this project to collect structured information
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setAddFormDialogOpen(true)}
                    >
                        Add Your First Form
                    </Button>
                </Paper>
            ) : filteredForms.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No matching forms
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Adjust or clear the filters to see more forms.
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={clearFilters}
                    >
                        Clear Filters
                    </Button>
                </Paper>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {formsByType.map((group) => <Paper key={group.key} variant="outlined" sx={{ overflow: 'hidden' }}>
                        <Button
                            fullWidth
                            onClick={() => setCollapsedFormTypes(previous => ({ ...previous, [group.key]: !previous[group.key] }))}
                            aria-expanded={!collapsedFormTypes[group.key]}
                            aria-label={`${collapsedFormTypes[group.key] ? 'Expand' : 'Collapse'} ${group.name} forms`}
                            endIcon={collapsedFormTypes[group.key] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                            sx={{ px: { xs: 1.5, sm: 2 }, py: 1, bgcolor: 'action.hover', color: 'text.primary', textTransform: 'none', justifyContent: 'space-between', '& .MuiButton-endIcon': { ml: 'auto' } }}
                        >
                            <Typography variant="subtitle2" component="span" sx={{ textAlign: 'left' }}>
                                {group.name} <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>({group.forms.length})</Box>
                            </Typography>
                        </Button>
                    <Collapse in={!collapsedFormTypes[group.key]} unmountOnExit>
                    {group.forms.map((form) => {
                        const formId = form.project_form_id;
                        const isOpening = openingFormId === formId;
                        const addedDate = form.created_date || form.saved_date;
                        const dateLabel = addedDate && !Number.isNaN(new Date(addedDate).getTime())
                            ? new Date(addedDate).toLocaleDateString('en-GB') : '';
                        const activityLabel = (form.activity_ids?.length > 0 || form.activity_id)
                            ? `Linked: ${getActivityLabel(form)}` : '';
                        const metadata = [dateLabel, form.worker_name, activityLabel].filter(Boolean).join(' · ');
                        return (
                            <Box
                                key={formId}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    px: { xs: 1, sm: 2 }, py: 0.5,
                                    borderTop: 1, borderColor: 'divider',
                                    '&:hover': { bgcolor: 'action.hover' }
                                }}
                            >
                                <AssignmentIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                                <Button
                                    onClick={() => handleViewForm(form)}
                                    disabled={isOpening}
                                    sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', color: 'text.primary' }}
                                >
                                    <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        <Box component="span" sx={{ fontWeight: 600 }}>{form.title || form.template_name || 'Untitled Form'}</Box>
                                        {metadata && <Box component="span" sx={{ color: 'text.secondary', ml: 1, fontWeight: 400 }}>
                                            {metadata}
                                        </Box>}
                                    </Box>
                                </Button>
                                {isOpening && <CircularProgress size={18} />}
                                {(form.activity_ids?.length > 0 || form.activity_id) && onOpenActivity && (
                                    <IconButton size="small" aria-label={`Open linked activity for ${form.title || form.template_name || 'form'}`} onClick={() => handleOpenLinkedActivity(form)}>
                                        <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                )}
                                <IconButton size="small" aria-label={`Export ${form.title || form.template_name || 'form'} as PDF`} onClick={() => handleExportPDF(form)}>
                                    <PdfIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" aria-label={`Delete ${form.title || form.template_name || 'form'}`} onClick={() => handleDeleteClick(form)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        );
                    })}
                    </Collapse>
                    </Paper>)}
                </Box>
            )}

            {/* Add Form Dialog */}
            <Dialog open={addFormDialogOpen} onClose={() => !isAddingForm && setAddFormDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Form to Project</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Select a form template to add to this project
                    </Typography>
                    
                    <FormControl fullWidth>
                        <InputLabel>Form Template</InputLabel>
                        <Select
                            value={selectedTemplate}
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                            label="Form Template"
                            disabled={isAddingForm}
                        >
                            {templates.map((template) => {
                                const existingCount = forms.filter(form => form.template_id === template.template_id).length;
                                return (
                                    <MenuItem key={`template-${template.template_id}`} value={template.template_id}>
                                        <Box>
                                            <Typography variant="body1">
                                                {template.name}
                                                {existingCount > 0 && (
                                                    <Chip 
                                                        label={`${existingCount} added`} 
                                                        size="small" 
                                                        variant="outlined" 
                                                        sx={{ ml: 1 }} 
                                                    />
                                                )}
                                            </Typography>
                                            {template.description && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {template.description}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" color="text.secondary">
                                                {template.form_schema?.fields?.length || 0} fields
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddFormDialogOpen(false)} disabled={isAddingForm}>Cancel</Button>
                    <Button onClick={handleAddForm} variant="contained" disabled={isAddingForm || !selectedTemplate}>
                        {isAddingForm ? <CircularProgress size={18} color="inherit" /> : 'Add Form'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View/Edit Form Dialog */}
            <Dialog open={viewFormDialogOpen} onClose={closeFormDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                        <Box>
                            <Typography variant="h6">
                                {isEditing ? 'Edit Form: ' : 'View Form: '}{selectedForm?.template_name}
                            </Typography>
                            {selectedFormIndex !== -1 && filteredForms.length > 1 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Form {selectedFormIndex + 1} of {filteredForms.length}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {filteredForms.length > 1 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <IconButton
                                        aria-label="Previous form"
                                        onClick={() => handleNavigateForm(-1)}
                                        disabled={!canNavigateToPreviousForm || openingFormId !== null || isNavigatingForms}
                                        size="small"
                                    >
                                        <ChevronLeftIcon />
                                    </IconButton>
                                    {isNavigatingForms && <CircularProgress size={18} sx={{ mx: 0.5 }} />}
                                    <IconButton
                                        aria-label="Next form"
                                        onClick={() => handleNavigateForm(1)}
                                        disabled={!canNavigateToNextForm || openingFormId !== null || isNavigatingForms}
                                        size="small"
                                    >
                                        <ChevronRightIcon />
                                    </IconButton>
                                </Box>
                            )}
                            {!isEditing ? (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        startIcon={<PdfIcon />}
                                        onClick={() => handleExportPDF(selectedForm)}
                                        variant="outlined"
                                        size="small"
                                        color="primary"
                                        disabled={isNavigatingForms}
                                    >
                                        Export PDF
                                    </Button>
                                    <Button
                                        startIcon={<EditIcon />}
                                        onClick={handleEditForm}
                                        variant="outlined"
                                        size="small"
                                        disabled={isNavigatingForms}
                                    >
                                        Edit
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        startIcon={<CancelIcon />}
                                        onClick={handleCancelEdit}
                                        variant="outlined"
                                        size="small"
                                        disabled={isNavigatingForms}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        startIcon={<SaveIcon />}
                                        onClick={handleSaveForm}
                                        variant="contained"
                                        size="small"
                                        disabled={isNavigatingForms}
                                    >
                                        Save
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {isNavigatingForms ? (
                        <Box sx={{ minHeight: 320, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                            <CircularProgress />
                            <Typography variant="body2" color="text.secondary">
                                Loading form...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                    {selectedForm?.template?.description && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            {selectedForm.template.description}
                        </Alert>
                    )}
                    {(selectedForm?.activity_ids?.length > 0 || selectedForm?.activity_id) && (
                        <Alert
                            severity="info"
                            sx={{ mb: 2 }}
                            action={onOpenActivity ? (
                                <Button
                                    color="inherit"
                                    size="small"
                                    endIcon={<OpenInNewIcon />}
                                    onClick={() => handleOpenLinkedActivity(selectedForm)}
                                >
                                    Open activity
                                </Button>
                            ) : null}
                        >
                            Linked to {selectedForm.activity_ids?.length > 1 ? 'activities' : 'activity'}:{' '}
                            {(selectedForm.activity_ids?.length ? selectedForm.activity_ids : [selectedForm.activity_id]).map((activityId, index) => (
                                <Button key={activityId} size="small" onClick={() => handleOpenLinkedActivity(selectedForm, activityId)} disabled={!onOpenActivity}>
                                    {activityId === selectedForm.activity_id ? getActivityLabel(selectedForm) : `Activity #${activityId}`}
                                </Button>
                            ))}
                        </Alert>
                    )}
                    
                    <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Title
                            </Typography>
                            {isEditing ? (
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Enter a title for this form"
                                    value={formTitle}
                                    onChange={(e) => setFormTitle(e.target.value)}
                                    onBlur={(e) => handleTitleCommit(e.target.value)}
                                />
                            ) : (
                                <Typography variant="body2" color={formTitle ? 'text.primary' : 'text.secondary'}>
                                    {formTitle || '(No title set)'}
                                </Typography>
                            )}
                        </Box>
                        {!selectedForm?.template?.form_schema?.fields ? (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                No form fields found. Please check the form template configuration.
                            </Alert>
                        ) : selectedForm.template.form_schema.fields.length === 0 ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                This form template has no fields configured.
                            </Alert>
                        ) : (
                            selectedForm.template.form_schema.fields.map((field, index) => (
                                <Box key={field.id} sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        {field.label}
                                        {field.required && (
                                            <span style={{ color: 'red', marginLeft: 4 }}>*</span>
                                        )}
                                    </Typography>
                                    
                                    {renderFormField(field, formResponses[field.id], isEditing)}
                                </Box>
                            ))
                        )}
                    </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {isEditing ? (
                        <>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                            <Button onClick={handleSaveForm} variant="contained">Save</Button>
                        </>
                    ) : (
                        <>
                            <Button 
                                onClick={() => handleExportPDF(selectedForm)} 
                                startIcon={<PdfIcon />}
                                color="primary"
                            >
                                Export PDF
                            </Button>
                            <Button onClick={closeFormDialog}>Close</Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Signature Dialog */}
            <Dialog 
                open={signatureDialogOpen} 
                onClose={handleSignatureDialogClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Digital Signature
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Please sign in the area below using your mouse, finger, or stylus.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Signer Name"
                        value={signatureName}
                        onChange={(e) => setSignatureName(e.target.value)}
                        required
                        sx={{ mb: 2 }}
                        disabled={!currentSignatureField}
                        placeholder="Enter full name of signer"
                    />
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            {currentSignatureField?.label || 'Signature'}
                        </Typography>
                        <Box sx={(theme) => ({
                            border: '2px solid',
                            borderColor: theme.palette.divider,
                            borderRadius: 1,
                            overflow: 'hidden',
                            mb: 2,
                            position: 'relative',
                            width: '100%',
                            height: 200
                        })}>
                            <SignatureCanvas
                                ref={signatureRef}
                                canvasProps={{
                                    width: 500,
                                    height: 200,
                                    className: 'signature-canvas',
                                    style: { 
                                        width: '100%', 
                                        height: '100%',
                                        touchAction: 'none',
                                        display: 'block',
                                        border: 'none',
                                        outline: 'none'
                                    }
                                }}
                                backgroundColor={theme.palette.background.paper}
                                penColor="black"
                                dotSize={1.5}
                                velocityFilterWeight={0.7}
                                minWidth={0.8}
                                maxWidth={2.5}
                            />
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClearSignature}
                            startIcon={<ClearIcon />}
                        >
                            Clear Signature
                        </Button>
                    </Paper>

                    <Alert severity="info">
                        Your signature will be saved as part of this form. 
                        By signing, you acknowledge the information provided.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSignatureDialogClose}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSaveSignature}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={!signatureName.trim()}
                    >
                        Save Signature
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteFormDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
                <DialogTitle>Delete Form</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Are you sure you want to delete this form?
                    </Typography>
                    {formToDelete && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formToDelete.title || formToDelete.template_name || 'Untitled Form'}
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                        This action cannot be undone. All form data and associated photos will be permanently deleted.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        variant="contained" 
                        color="error"
                        startIcon={<DeleteIcon />}
                    >
                        Delete Form
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={discardNavigationDialogOpen}
                onClose={handleDiscardNavigationCancel}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Discard unsaved changes?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        You have unsaved changes on this form. Continue to the next form without saving?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDiscardNavigationCancel}>Keep editing</Button>
                    <Button onClick={handleDiscardNavigationConfirm} color="warning" variant="contained">
                        Discard changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Snackbar */}
            <Snackbar
                open={!!success}
                autoHideDuration={6000}
                onClose={() => setSuccess('')}
                message={success}
            />
        </Box>
    );
};

export default ProjectFormsTab;
